/**
 * @file: uploadChatMedia.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Upload helpers that turn locally-picked assets into chat message
 * payloads. Image/video go through {@link MediaFileUploadController} so we
 * reuse its concurrency control + (on native) video compression and
 * client-side thumbnail generation. Files and audio are simpler one-shot
 * uploads via {@link MediaFileService}.
 *
 * The media service returns dimensions + a generated cover/poster +
 * duration (in SECONDS) on `result.extra`; we normalize duration to
 * milliseconds to match the `ChatMediaItem.durationMs` contract.
 */

import { MediaFileUploadController } from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import {
  ChatMediaItem,
  ChatMediaKind,
  ChatMessagePayloadAudio,
  ChatMessagePayloadFile,
} from '@euler/model/chat';
import { MediaFileService } from '@euler/services';

/** Storage sub-path for all chat attachments. */
const CHAT_MEDIA_REALM = 'files/chat';

/**
 * True for a hosted (already-uploaded) URL. A `file://`/`blob:`/`data:` URI
 * is a local device asset that must be uploaded before it can be sent — see
 * the retry path, which must never persist a local URI to the server.
 */
export function isRemoteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/** Best-effort audio MIME from a recording URI's extension. */
export function audioMimeFromUri(uri: string): string {
  const ext = uri.split('?')[0].split('.').pop()?.toLowerCase();
  if (ext === 'wav') return 'audio/wav';
  if (ext === 'webm') return 'audio/webm';
  return 'audio/aac';
}

export type ChatMediaUploadAsset = {
  kind: ChatMediaKind;
  uri: string;
  mimeType: string;
};

function toChatMediaItem(
  kind: ChatMediaKind,
  result: {
    url: string;
    extra?: {
      width?: number;
      height?: number;
      cover?: string;
      duration?: number;
    } | null;
  },
): ChatMediaItem {
  const extra = result.extra ?? {};
  return {
    kind,
    url: result.url,
    coverUrl: extra.cover || undefined,
    width: typeof extra.width === 'number' ? extra.width : undefined,
    height: typeof extra.height === 'number' ? extra.height : undefined,
    durationMs:
      kind === 'video' && typeof extra.duration === 'number'
        ? Math.round(extra.duration * 1000)
        : undefined,
  };
}

export type ChatMediaUploader = {
  /** Upload one asset; resolves to the typed media item. */
  upload: (asset: ChatMediaUploadAsset) => Promise<ChatMediaItem>;
  dispose: () => void;
};

/**
 * Create a batch uploader backed by a single
 * {@link MediaFileUploadController}. Call `upload()` once per asset — the
 * controller runs up to its concurrency limit in parallel and queues the
 * rest. `dispose()` aborts anything in flight.
 */
export function createChatMediaUploader(
  mediaFileService: MediaFileService,
): ChatMediaUploader {
  const controller = new MediaFileUploadController<undefined>({
    mediaFileService,
  });
  return {
    async upload(asset) {
      return await new Promise<ChatMediaItem>((resolve, reject) => {
        controller.enqueue({
          type: asset.kind,
          localFileUri: asset.uri,
          contentType: asset.mimeType,
          parameters: {
            realm: CHAT_MEDIA_REALM,
            cover: true,
            blurhash: true,
            coverSize: 600,
          },
          onSuccess: data => resolve(toChatMediaItem(asset.kind, data.result)),
          listener: e => {
            if (e.type === 'error') reject(e.error);
            else if (e.type === 'abort') {
              reject(e.error ?? new Error('上传已取消'));
            }
          },
        });
      });
    },
    dispose: () => controller.dispose(),
  };
}

/** Upload an arbitrary document and build a `file` payload. */
export async function uploadChatFile(
  mediaFileService: MediaFileService,
  file: {
    uri: string;
    filename: string;
    mimeType: string;
    sizeInBytes: number;
  },
): Promise<ChatMessagePayloadFile> {
  const result = await mediaFileService.upload(file.uri, {
    realm: CHAT_MEDIA_REALM,
    filename: file.filename,
    mimeType: file.mimeType,
  });
  return {
    type: 'file',
    url: result.url,
    filename: file.filename,
    mimeType: file.mimeType,
    sizeInBytes: file.sizeInBytes,
  };
}

/** Upload a voice clip and build an `audio` payload. */
export async function uploadChatAudio(
  mediaFileService: MediaFileService,
  audio: {
    uri: string;
    mimeType: string;
    durationMs: number;
    /** Slim normalized amplitude strip captured while recording. */
    waveform?: number[];
  },
): Promise<ChatMessagePayloadAudio> {
  const result = await mediaFileService.upload(audio.uri, {
    realm: CHAT_MEDIA_REALM,
    mimeType: audio.mimeType,
  });
  const extra = (result.extra ?? {}) as { duration?: number };
  const durationMs =
    typeof extra.duration === 'number' && extra.duration > 0
      ? Math.round(extra.duration * 1000)
      : audio.durationMs;
  return {
    type: 'audio',
    url: result.url,
    durationMs,
    waveform: audio.waveform,
  };
}
